import React, { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Row, Col, Card, CardBody, CardTitle, Button, Spinner, Alert,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input,
  Badge, Table, Nav, NavItem, NavLink, Collapse,
} from "reactstrap"
import Switch from "react-switch"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import XLSX from "xlsx-js-style"
import "./Empresas.scss"

/* ── Tipos de documento DIAN ── */
const TIPOS_DOCUMENTO = {
  "01": "Factura electrónica de Venta",
  "02": "Factura electrónica de venta - exportación",
  "03": "Instrumento electrónico de transmisión – tipo 03",
  "04": "Factura electrónica de Venta - tipo 04",
  "05": "Documento soporte en adquisiciones efectuadas a sujetos no obligados a expedir factura o documento equivalente",
  "91": "Nota Crédito",
  "92": "Nota Débito",
  "93": "Nota de Ajuste de tipo debito al Documento Equivalente",
  "94": "Nota de Ajuste de tipo crédito al Documento Equivalente",
  "95": "Nota de ajuste al documento soporte en adquisiciones efectuadas a sujetos no obligados a expedir factura o documento equivalente",
  "102": "Nomina Individual",
  "103": "Nomina Individual de Ajuste",
}

/* ── styles ── */
const labelStyle = { fontWeight: 600, fontSize: 13, color: "#495057", marginBottom: 4 }
const inputStyle = { borderRadius: 8, fontSize: 13 }

const emptyResolucion = {
  numeroResolucion: "",
  fechaResolucion: "",
  prefijo: "",
  rangoDesde: "",
  rangoHasta: "",
  consecutivoActual: "0",
  validaDesde: "",
  validaHasta: "",
  claveTecnica: "",
  tipoDocumento: "",
  activo: true,
}

const Resoluciones = ({ setBreadcrumbItems, user }) => {
  document.title = "Resoluciones | LS-DA - Softlogy"

  const { id } = useParams() // empresaId
  const navigate = useNavigate()

  const [resoluciones, setResoluciones] = useState([])
  const [empresaNombre, setEmpresaNombre] = useState("")
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" })

  // Modal
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState("create")
  const [formData, setFormData] = useState({ ...emptyResolucion })
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [syncing, setSyncing] = useState(false)

  // Sucursales & assign
  const [sucursales, setSucursales] = useState([])
  const [prefijosAsignados, setPrefijosAsignados] = useState([])
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [assignPrefijo, setAssignPrefijo] = useState(null)
  const [selectedSucursalIds, setSelectedSucursalIds] = useState([])
  const [assigning, setAssigning] = useState(false)

  // NC/ND factura association
  const [selectedFacturaPrefijos, setSelectedFacturaPrefijos] = useState([])
  const [ncNdManualMode, setNcNdManualMode] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Search
  const [searchTerm, setSearchTerm] = useState("")

  // Tabs & group view
  const [activeTab, setActiveTab] = useState("todas")
  const [collapsedGroups, setCollapsedGroups] = useState({})

  const toggleGroup = (tipo) => {
    setCollapsedGroups(prev => ({ ...prev, [tipo]: !prev[tipo] }))
  }

  // ─── Fetch resoluciones ───
  const fetchResoluciones = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axiosClient.get(`/empresas/${id}/resoluciones`)
      setResoluciones(res.data?.data || res.data || [])
    } catch (err) {
      setAlert({ show: true, message: "Error al cargar resoluciones: " + (err.response?.data?.message || err.message), variant: "danger" })
      setResoluciones([])
    } finally {
      setLoading(false)
    }
  }, [id])

  // ─── Fetch empresa ───
  const fetchEmpresa = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/empresas/${id}`)
      const emp = res.data?.data || res.data
      setEmpresaNombre(emp?.nombre || "")
    } catch (e) { /* ignore */ }
  }, [id])

  // ─── Fetch sucursales ───
  const fetchSucursales = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/empresas/${id}/sucursales`)
      setSucursales(res.data?.data || res.data || [])
    } catch (e) { setSucursales([]) }
  }, [id])

  // ─── Fetch prefijo-sucursal assignments ───
  const fetchAsignaciones = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/empresas/${id}/prefijos-sucursales`)
      setPrefijosAsignados(res.data?.data || res.data || [])
    } catch (e) { setPrefijosAsignados([]) }
  }, [id])

  // ─── Init ───
  useEffect(() => {
    const greeting = user ? `Bienvenido, ${user.nombre || "Usuario"}` : "Bienvenido"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Empresas", link: "/portal/consultar-empresas" },
      { title: "Resoluciones", link: "#" },
    ])
    fetchResoluciones()
    fetchEmpresa()
    fetchSucursales()
    fetchAsignaciones()
  }, []) // eslint-disable-line

  // ─── Helpers ───
  const formatDate = (dateStr) => {
    if (!dateStr) return "—"
    return new Date(dateStr).toLocaleDateString("es-CO", { timeZone: "UTC" })
  }

  const isExpired = (dateStr) => {
    if (!dateStr) return false
    return new Date(dateStr) < new Date()
  }

  const getUsagePercent = (desde, hasta, actual) => {
    const d = parseInt(desde) || 0
    const h = parseInt(hasta) || 0
    const a = parseInt(actual) || 0
    if (h <= d || a <= d) return 0
    return Math.min(100, Math.round(((a - d) / (h - d)) * 100))
  }

  // ─── Modal ───
  const openCreateModal = () => {
    setModalMode("create")
    setFormData({ ...emptyResolucion })
    setEditingId(null)
    setSelectedFacturaPrefijos([])
    setNcNdManualMode(false)
    setModalOpen(true)
  }

  const openEditModal = (res) => {
    setModalMode("edit")
    setFormData({
      numeroResolucion: res.numeroResolucion || "",
      fechaResolucion: res.fechaResolucion ? res.fechaResolucion.slice(0, 10) : "",
      prefijo: res.prefijo || "",
      rangoDesde: res.rangoDesde || "",
      rangoHasta: res.rangoHasta || "",
      consecutivoActual: res.consecutivoActual || "0",
      validaDesde: res.validaDesde ? res.validaDesde.slice(0, 10) : "",
      validaHasta: res.validaHasta ? res.validaHasta.slice(0, 10) : "",
      claveTecnica: res.claveTecnica || "",
      tipoDocumento: res.tipoDocumento || "",
      activo: res.activo !== false,
    })
    setEditingId(res.id)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setFormData({ ...emptyResolucion })
    setEditingId(null)
    setSelectedFacturaPrefijos([])
    setNcNdManualMode(false)
  }

  const setFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // ─── CRUD ───
  const isNcNdTipo = (tipo) => ["91", "92", "93", "94", "95"].includes(tipo)

  const facturasElectronicas = resoluciones.filter(r => r.tipoDocumento === "01")

  const toggleFacturaPrefijo = (resId) => {
    setSelectedFacturaPrefijos(prev =>
      prev.includes(resId) ? prev.filter(x => x !== resId) : [...prev, resId]
    )
  }

  const handleCreate = async () => {
    if (!formData.prefijo) {
      setAlert({ show: true, message: "El Prefijo es obligatorio", variant: "warning" })
      return
    }

    // NC/ND mode: create one per selected factura
    if (isNcNdTipo(formData.tipoDocumento) && !ncNdManualMode && selectedFacturaPrefijos.length > 0) {
      setSaving(true)
      let created = 0, errors = 0
      try {
        for (const facturaId of selectedFacturaPrefijos) {
          const factura = resoluciones.find(r => r.id === facturaId)
          if (!factura) continue
          const newRes = {
            numeroResolucion: factura.numeroResolucion,
            prefijo: formData.prefijo,
            tipoDocumento: formData.tipoDocumento,
            rangoDesde: parseInt(factura.rangoDesde) || 0,
            rangoHasta: parseInt(factura.rangoHasta) || 0,
            consecutivoActual: 0,
            fechaResolucion: factura.fechaResolucion ? factura.fechaResolucion.slice(0, 10) : "",
            validaDesde: factura.validaDesde ? factura.validaDesde.slice(0, 10) : "",
            validaHasta: factura.validaHasta ? factura.validaHasta.slice(0, 10) : "",
            claveTecnica: factura.claveTecnica || "",
            activo: true,
          }
          try {
            await axiosClient.post(`/empresas/${id}/resoluciones`, newRes)
            // Copy sucursal assignments from factura prefijo
            const facAsigs = prefijosAsignados.filter(a => a.prefijo === factura.prefijo)
            if (facAsigs.length > 0) {
              const asignaciones = facAsigs.map(a => ({ prefijo: formData.prefijo, sucursalId: a.sucursalId }))
              try { await axiosClient.put(`/empresas/${id}/prefijos-sucursales`, { asignaciones }) } catch (e) { /* ignore */ }
            }
            created++
          } catch (e) { errors++ }
        }
        setAlert({ show: true, message: `${created} resolución(es) creada(s)${errors > 0 ? `, ${errors} error(es)` : ""}`, variant: errors > 0 ? "warning" : "success" })
        closeModal()
        fetchResoluciones()
        fetchAsignaciones()
      } catch (err) {
        setAlert({ show: true, message: "Error al crear: " + (err.response?.data?.message || err.message), variant: "danger" })
      } finally {
        setSaving(false)
      }
      return
    }

    // NC/ND manual mode: fixed values
    if (isNcNdTipo(formData.tipoDocumento) && ncNdManualMode) {
      setSaving(true)
      try {
        const today = new Date().toISOString().slice(0, 10)
        const futureDate = new Date()
        futureDate.setFullYear(futureDate.getFullYear() + 50)
        const validaHasta50 = futureDate.toISOString().slice(0, 10)
        await axiosClient.post(`/empresas/${id}/resoluciones`, {
          numeroResolucion: "NA",
          prefijo: formData.prefijo,
          tipoDocumento: formData.tipoDocumento,
          rangoDesde: 1,
          rangoHasta: 9999999,
          consecutivoActual: 0,
          fechaResolucion: today,
          validaDesde: today,
          validaHasta: validaHasta50,
          claveTecnica: "NA",
          activo: true,
        })
        setAlert({ show: true, message: "Resolución creada correctamente (sin asociar)", variant: "success" })
        closeModal()
        fetchResoluciones()
      } catch (err) {
        setAlert({ show: true, message: "Error al crear: " + (err.response?.data?.message || err.message), variant: "danger" })
      } finally {
        setSaving(false)
      }
      return
    }

    // Normal mode
    if (!formData.numeroResolucion) {
      setAlert({ show: true, message: "Número de resolución es obligatorio", variant: "warning" })
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...formData,
        rangoDesde: parseInt(formData.rangoDesde) || 0,
        rangoHasta: parseInt(formData.rangoHasta) || 0,
        consecutivoActual: parseInt(formData.consecutivoActual) || 0,
      }
      await axiosClient.post(`/empresas/${id}/resoluciones`, payload)
      setAlert({ show: true, message: "Resolución creada correctamente", variant: "success" })
      closeModal()
      fetchResoluciones()
    } catch (err) {
      setAlert({ show: true, message: "Error al crear: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    if (!formData.numeroResolucion || !formData.prefijo) {
      setAlert({ show: true, message: "Número de resolución y Prefijo son obligatorios", variant: "warning" })
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...formData,
        rangoDesde: parseInt(formData.rangoDesde) || 0,
        rangoHasta: parseInt(formData.rangoHasta) || 0,
        consecutivoActual: parseInt(formData.consecutivoActual) || 0,
      }
      await axiosClient.put(`/empresas/${id}/resoluciones/${editingId}`, payload)
      setAlert({ show: true, message: "Resolución actualizada correctamente", variant: "success" })
      closeModal()
      fetchResoluciones()
    } catch (err) {
      setAlert({ show: true, message: "Error al actualizar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  // ─── Sync DIAN ───
  const handleSyncDian = async () => {
    setSyncing(true)
    try {
      const res = await axiosClient.post(`/empresas/${id}/resoluciones/sync`)
      const data = res.data?.data || []
      const message = res.data?.message || `${data.length} resoluciones sincronizadas`
      setAlert({ show: true, message, variant: "success" })
      fetchResoluciones()
    } catch (err) {
      setAlert({ show: true, message: "Error al sincronizar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setSyncing(false)
    }
  }

  // ─── Assign Prefijo to Sucursal ───
  const [originalSucursalIds, setOriginalSucursalIds] = useState([])

  const openAssignModal = (res) => {
    setAssignPrefijo(res)
    const currentIds = prefijosAsignados.filter(a => a.prefijo === res.prefijo).map(a => a.sucursalId)
    setSelectedSucursalIds(currentIds)
    setOriginalSucursalIds(currentIds)
    setAssignModalOpen(true)
  }

  const toggleSucursalSelection = (sucId) => {
    setSelectedSucursalIds(prev =>
      prev.includes(sucId) ? prev.filter(id => id !== sucId) : [...prev, sucId]
    )
  }

  const handleAssignSucursal = async () => {
    if (!assignPrefijo) return
    setAssigning(true)
    try {
      const added = selectedSucursalIds.filter(id => !originalSucursalIds.includes(id))
      const removed = originalSucursalIds.filter(id => !selectedSucursalIds.includes(id))

      // DELETE removed assignments
      for (const sucId of removed) {
        await axiosClient.delete(`/empresas/${id}/prefijos-sucursales`, {
          data: { prefijo: assignPrefijo.prefijo, sucursalId: sucId }
        })
      }

      // PUT new assignments
      if (added.length > 0) {
        const asignaciones = added.map(sucId => ({
          prefijo: assignPrefijo.prefijo,
          sucursalId: sucId,
        }))
        await axiosClient.put(`/empresas/${id}/prefijos-sucursales`, { asignaciones })
      }

      const totalChanges = added.length + removed.length
      setAlert({ show: true, message: totalChanges > 0 ? `Prefijo ${assignPrefijo.prefijo}: ${added.length} asignada(s), ${removed.length} desasignada(s)` : "Sin cambios", variant: totalChanges > 0 ? "success" : "info" })
      setAssignModalOpen(false)
      fetchResoluciones()
      fetchAsignaciones()
    } catch (err) {
      setAlert({ show: true, message: "Error al asignar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setAssigning(false)
    }
  }

  const handleDelete = async (resId, numRes) => {
    if (!window.confirm(`¿Está seguro de eliminar la resolución "${numRes}"?`)) return
    setDeletingId(resId)
    try {
      await axiosClient.delete(`/empresas/${id}/resoluciones/${resId}`)
      setAlert({ show: true, message: "Resolución eliminada correctamente", variant: "success" })
      fetchResoluciones()
    } catch (err) {
      setAlert({ show: true, message: "Error al eliminar: " + (err.response?.data?.message || err.message), variant: "danger" })
    } finally {
      setDeletingId(null)
    }
  }

  // ─── Export Excel ───
  const handleExportExcel = () => {
    if (resoluciones.length === 0) return

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12, name: "Calibri" },
      fill: { fgColor: { rgb: "2B3A67" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } },
      },
    }
    const cellBorder = {
      top: { style: "thin", color: { rgb: "DEE2E6" } },
      bottom: { style: "thin", color: { rgb: "DEE2E6" } },
      left: { style: "thin", color: { rgb: "DEE2E6" } },
      right: { style: "thin", color: { rgb: "DEE2E6" } },
    }
    const evenRow = { fill: { fgColor: { rgb: "F8F9FA" } }, border: cellBorder, font: { name: "Calibri", sz: 11 }, alignment: { vertical: "center" } }
    const oddRow = { fill: { fgColor: { rgb: "FFFFFF" } }, border: cellBorder, font: { name: "Calibri", sz: 11 }, alignment: { vertical: "center" } }

    const titleRow = [{
      v: `Resoluciones - ${empresaNombre}`, s: {
        font: { bold: true, sz: 16, color: { rgb: "2B3A67" }, name: "Calibri" },
        alignment: { horizontal: "center" },
      }
    }]
    const dateRow = [{
      v: `Generado: ${new Date().toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}`, s: {
        font: { italic: true, sz: 10, color: { rgb: "6C757D" }, name: "Calibri" },
        alignment: { horizontal: "center" },
      }
    }]

    const headers = ["No. Resolución", "Prefijo", "Rango Desde", "Rango Hasta", "Consecutivo", "Fecha Resolución", "Válida Desde", "Válida Hasta", "Tipo Documento", "Clave Técnica", "Sucursales Asignadas", "Estado"]
    const headerRow = headers.map(h => ({ v: h, s: headerStyle }))

    const dataRows = resoluciones.map((res, i) => {
      const style = i % 2 === 0 ? evenRow : oddRow
      const centerStyle = { ...style, alignment: { ...style.alignment, horizontal: "center" } }
      return [
        { v: res.numeroResolucion || "", s: { ...style, font: { ...style.font, bold: true } } },
        { v: res.prefijo || "", s: centerStyle },
        { v: res.rangoDesde || "", s: centerStyle },
        { v: res.rangoHasta || "", s: centerStyle },
        { v: res.consecutivoActual || "0", s: centerStyle },
        { v: formatDate(res.fechaResolucion), s: centerStyle },
        { v: formatDate(res.validaDesde), s: centerStyle },
        {
          v: formatDate(res.validaHasta), s: {
            ...centerStyle,
            font: { ...style.font, color: { rgb: isExpired(res.validaHasta) ? "DC3545" : "198754" } }
          }
        },
        { v: res.tipoDocumento ? `${res.tipoDocumento} - ${TIPOS_DOCUMENTO[res.tipoDocumento] || res.tipoDocumento}` : "—", s: style },
        { v: res.claveTecnica || "", s: { ...style, font: { ...style.font, sz: 9 } } },
        {
          v: (() => {
            const asigs = prefijosAsignados.filter(a => a.prefijo === res.prefijo)
            if (asigs.length === 0) return "Sin asignar"
            return asigs.map(a => a.sucursal?.nombre || "N/A").join(", ")
          })(), s: style
        },
        {
          v: res.activo ? "Activo" : "Inactivo", s: {
            ...centerStyle,
            font: { ...style.font, color: { rgb: res.activo ? "198754" : "DC3545" }, bold: true },
          }
        },
      ]
    })

    const wsData = [titleRow, dateRow, [], headerRow, ...dataRows]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } },
    ]
    ws["!cols"] = [
      { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 32 }, { wch: 40 }, { wch: 28 }, { wch: 10 },
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Resoluciones")
    XLSX.writeFile(wb, `Resoluciones_${empresaNombre || "empresa"}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // ─── Stats ───
  const stats = {
    total: resoluciones.length,
    vigentes: resoluciones.filter(r => r.activo && !isExpired(r.validaHasta)).length,
    vencidas: resoluciones.filter(r => isExpired(r.validaHasta)).length,
    usoAlto: resoluciones.filter(r => getUsagePercent(r.rangoDesde, r.rangoHasta, r.consecutivoActual) > 90).length,
    sinConfigurar: resoluciones.filter(r => !prefijosAsignados.some(a => a.prefijo === r.prefijo) || !r.tipoDocumento).length,
  }

  // ─── Tab-based filtering ───
  const getTabData = () => {
    let base = resoluciones
    switch (activeTab) {
      case "vigentes": base = base.filter(r => r.activo && !isExpired(r.validaHasta)); break
      case "vencidas": base = base.filter(r => isExpired(r.validaHasta)); break
      case "sinConfigurar": base = base.filter(r => !prefijosAsignados.some(a => a.prefijo === r.prefijo) || !r.tipoDocumento); break
      default: break
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      base = base.filter(r =>
        (r.numeroResolucion || "").toLowerCase().includes(term) ||
        (r.prefijo || "").toLowerCase().includes(term) ||
        (r.tipoDocumento || "").toLowerCase().includes(term) ||
        (TIPOS_DOCUMENTO[r.tipoDocumento] || "").toLowerCase().includes(term)
      )
    }
    return base
  }

  const filtered = getTabData()
  const totalPages = Math.ceil(filtered.length / itemsPerPage)
  const paginatedItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ─── Grouped by tipo ───
  const groupedByTipo = filtered.reduce((acc, r) => {
    const tipo = r.tipoDocumento || "SIN_TIPO"
    if (!acc[tipo]) acc[tipo] = []
    acc[tipo].push(r)
    return acc
  }, {})
  const sortedTipos = Object.keys(groupedByTipo).sort((a, b) => {
    if (a === "SIN_TIPO") return 1
    if (b === "SIN_TIPO") return -1
    return parseInt(a) - parseInt(b)
  })

  // ─── Reusable table row ───
  const renderResRow = (res) => {
    const usage = getUsagePercent(res.rangoDesde, res.rangoHasta, res.consecutivoActual)
    const expired = isExpired(res.validaHasta)
    const noSucursal = !prefijosAsignados.some(a => a.prefijo === res.prefijo)
    const noTipo = !res.tipoDocumento
    const needsAttention = noSucursal || noTipo
    return (
      <tr key={res.id} style={needsAttention ? { background: "#fffdf5", borderLeft: "3px solid #f1b44c" } : {}}>
        <td><strong>{res.numeroResolucion}</strong></td>
        <td><Badge color="primary" pill style={{ fontSize: 12 }}>{res.prefijo}</Badge></td>
        <td style={{ fontSize: 11 }}>{res.rangoDesde} — {res.rangoHasta}</td>
        <td style={{ textAlign: "center" }}><strong>{res.consecutivoActual || "0"}</strong></td>
        <td style={{ minWidth: 80 }}>
          <div style={{ background: "#e9ecef", borderRadius: 4, height: 6 }}>
            <div style={{ background: usage > 90 ? "#dc3545" : usage > 70 ? "#ffc107" : "#34c38f", borderRadius: 4, height: 6, width: `${usage}%`, transition: "width 0.3s" }} />
          </div>
          <span style={{ fontSize: 10, color: "#6c757d" }}>{usage}%</span>
        </td>
        <td style={{ fontSize: 11 }}>{formatDate(res.fechaResolucion)}</td>
        <td style={{ fontSize: 11 }}>
          <span style={{ color: expired ? "#dc3545" : "#495057" }}>
            {formatDate(res.validaDesde)} — {formatDate(res.validaHasta)}
          </span>
          {res.validaHasta && (() => {
            const diff = Math.ceil((new Date(res.validaHasta) - new Date()) / (1000 * 60 * 60 * 24))
            if (diff < 0) return <><br /><Badge color="danger" pill style={{ fontSize: 9 }}>Vencida hace {Math.abs(diff)} días</Badge></>
            const color = diff > 90 ? "success" : diff > 30 ? "warning" : "danger"
            return <><br /><Badge color={color} pill style={{ fontSize: 9 }}>{diff} días restantes</Badge></>
          })()}
        </td>
        <td style={{ fontSize: 11 }}>
          {res.tipoDocumento
            ? <>{res.tipoDocumento} - {TIPOS_DOCUMENTO[res.tipoDocumento] || res.tipoDocumento}</>
            : <span style={{ color: "#f1b44c", fontWeight: 600 }}><i className="mdi mdi-alert-outline me-1" />Sin tipo</span>}
        </td>
        <td style={{ fontSize: 10, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={res.claveTecnica || ""}>
          {res.claveTecnica ? <code style={{ fontSize: 10, background: "#f0f1f3", padding: "2px 4px", borderRadius: 4 }}>{res.claveTecnica}</code> : <span style={{ color: "#adb5bd" }}>—</span>}
        </td>
        <td>
          {(() => {
            const asigs = prefijosAsignados.filter(a => a.prefijo === res.prefijo)
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                {asigs.length > 0 ? (
                  <>
                    {asigs.map(a => (
                      <Badge key={a.id} color="info" pill style={{ fontSize: 10 }}><i className="mdi mdi-store me-1" />{a.sucursal?.nombre || a.sucursalId.slice(0, 8)}</Badge>
                    ))}
                    <Button color="link" size="sm" style={{ padding: 0, fontSize: 11, color: "#556ee6" }} onClick={() => openAssignModal(res)} title="Editar"><i className="mdi mdi-pencil" /></Button>
                  </>
                ) : (
                  <Button color="outline-primary" size="sm" style={{ borderRadius: 6, fontSize: 10 }} onClick={() => openAssignModal(res)}><i className="mdi mdi-link-variant me-1" />Asignar</Button>
                )}
              </div>
            )
          })()}
        </td>
        <td><Badge color={res.activo ? "success" : "secondary"} pill>{res.activo ? "Activo" : "Inactivo"}</Badge></td>
        <td>
          <Button color="warning" size="sm" className="me-1" style={{ borderRadius: 6 }} onClick={() => openEditModal(res)}><i className="mdi mdi-pencil" /></Button>
          <Button color="danger" size="sm" style={{ borderRadius: 6 }} disabled={deletingId === res.id} onClick={() => handleDelete(res.id, res.numeroResolucion)}>
            {deletingId === res.id ? <Spinner size="sm" /> : <i className="mdi mdi-delete" />}
          </Button>
        </td>
      </tr>
    )
  }

  const tableHead = (
    <thead style={{ background: "#f8f9fa" }}>
      <tr>
        <th>No. Resolución</th><th>Prefijo</th><th>Rango</th><th>Consecutivo</th><th>Uso</th>
        <th>Fecha Resolución</th><th>Vigencia</th><th>Tipo</th><th>Clave Técnica</th>
        <th>Sucursal</th><th>Estado</th><th style={{ width: 120 }}>Acciones</th>
      </tr>
    </thead>
  )

  return (
    <React.Fragment>
      {alert.show && (
        <Alert color={alert.variant} toggle={() => setAlert({ ...alert, show: false })}
          style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 320, boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col xs={12}>
          <Card style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,.08)" }}>
            <CardBody>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <CardTitle className="h4 mb-1">
                    <i className="mdi mdi-file-document-outline me-2" style={{ color: "#556ee6" }} />
                    Resoluciones
                  </CardTitle>
                  {empresaNombre && (
                    <p style={{ color: "#6c757d", marginBottom: 0, fontSize: 14 }}>
                      Empresa: <strong>{empresaNombre}</strong>
                    </p>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" onClick={openCreateModal} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-plus me-1" /> Nueva Resolución
                  </Button>
                  <Button color="info" onClick={handleSyncDian} disabled={syncing} style={{ borderRadius: 8 }}>
                    {syncing ? <><Spinner size="sm" /> Sincronizando...</> : <><i className="mdi mdi-sync me-1" /> Sincronizar DIAN</>}
                  </Button>
                  <Button color="success" onClick={handleExportExcel} disabled={resoluciones.length === 0} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-file-excel-outline me-1" /> Exportar Excel
                  </Button>
                  <Button color="secondary" outline onClick={() => navigate("/portal/consultar-empresas")} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-arrow-left me-1" /> Volver
                  </Button>
                </div>
              </div>

              {/* Dashboard Cards */}
              {!loading && resoluciones.length > 0 && (
                <Row className="mb-3 g-3">
                  {[
                    { label: "Total", value: stats.total, icon: "mdi-file-document-multiple-outline", color: "#556ee6", bg: "#eef2ff", tab: "todas" },
                    { label: "Vigentes", value: stats.vigentes, icon: "mdi-check-circle-outline", color: "#34c38f", bg: "#e8f8f0", tab: "vigentes" },
                    { label: "Vencidas", value: stats.vencidas, icon: "mdi-clock-alert-outline", color: "#dc3545", bg: "#fdecea", tab: "vencidas" },
                    { label: "Uso >90%", value: stats.usoAlto, icon: "mdi-chart-donut", color: "#f1b44c", bg: "#fff8e6" },
                    { label: "Sin Configurar", value: stats.sinConfigurar, icon: "mdi-cog-outline", color: "#e67e22", bg: "#fef5ee", tab: "sinConfigurar" },
                  ].map((card, i) => (
                    <Col key={i}>
                      <div onClick={() => { if (card.tab) { setActiveTab(card.tab); setCurrentPage(1) } }}
                        style={{
                          background: card.tab && activeTab === card.tab ? card.bg : "#fff",
                          border: `1px solid ${card.tab && activeTab === card.tab ? card.color + "40" : "#e9ecef"}`,
                          borderRadius: 10, padding: "14px 16px",
                          cursor: card.tab ? "pointer" : "default",
                          transition: "all 0.2s",
                        }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: card.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className={`mdi ${card.icon}`} style={{ fontSize: 20, color: card.color }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: "#343a40", lineHeight: 1.1 }}>{card.value}</div>
                            <div style={{ fontSize: 11, color: "#6c757d", fontWeight: 500 }}>{card.label}</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Tabs + Search */}
              {!loading && resoluciones.length > 0 && (
                <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-2">
                  <Nav tabs style={{ border: "none", marginBottom: -1 }}>
                    {[
                      { id: "todas", label: "Todas", icon: "mdi-view-list", count: stats.total },
                      { id: "porTipo", label: "Por Tipo", icon: "mdi-file-tree", count: sortedTipos.length + " tipos" },
                      { id: "vigentes", label: "Vigentes", icon: "mdi-check-circle-outline", count: stats.vigentes },
                      { id: "vencidas", label: "Vencidas", icon: "mdi-clock-alert-outline", count: stats.vencidas },
                      { id: "sinConfigurar", label: "Sin Configurar", icon: "mdi-cog-outline", count: stats.sinConfigurar },
                    ].map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          className={activeTab === tab.id ? "active" : ""}
                          onClick={() => { setActiveTab(tab.id); setCurrentPage(1) }}
                          style={{
                            cursor: "pointer", fontSize: 13, borderRadius: "8px 8px 0 0",
                            color: activeTab === tab.id ? "#556ee6" : "#6c757d",
                            fontWeight: activeTab === tab.id ? 600 : 400,
                            padding: "8px 14px",
                          }}>
                          <i className={`mdi ${tab.icon} me-1`} />
                          {tab.label}{" "}
                          <Badge color={activeTab === tab.id ? "primary" : "light"} pill
                            style={{ fontSize: 10, marginLeft: 4, color: activeTab === tab.id ? "#fff" : "#495057" }}>
                            {tab.count}
                          </Badge>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                  <div style={{ maxWidth: 280, minWidth: 200 }}>
                    <Input type="text" placeholder="Buscar resolución, prefijo, tipo..."
                      style={{ ...inputStyle, fontSize: 12 }} value={searchTerm}
                      onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }} />
                  </div>
                </div>
              )}

              {/* Content */}
              {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2" style={{ color: "#6c757d" }}>Cargando resoluciones...</p></div>
              ) : resoluciones.length === 0 ? (
                <div className="text-center py-5">
                  <i className="mdi mdi-file-document-remove-outline" style={{ fontSize: 48, color: "#c3cbe4" }} />
                  <p className="mt-2" style={{ color: "#6c757d" }}>No hay resoluciones registradas</p>
                  <Button color="primary" size="sm" onClick={openCreateModal}>Crear la primera resolución</Button>
                </div>
              ) : activeTab === "porTipo" ? (
                /* ── Grouped by Tipo view ── */
                <div style={{ paddingTop: 8 }}>
                  {sortedTipos.length === 0 ? (
                    <div className="text-center py-4" style={{ color: "#6c757d" }}>No se encontraron resultados</div>
                  ) : sortedTipos.map(tipo => {
                    const items = groupedByTipo[tipo]
                    const isOpen = collapsedGroups[tipo] !== false
                    const tipoLabel = tipo === "SIN_TIPO" ? "Sin Tipo de Documento" : `${tipo} - ${TIPOS_DOCUMENTO[tipo] || tipo}`
                    const tipoColor = tipo === "SIN_TIPO" ? "#f1b44c" : "#556ee6"
                    return (
                      <div key={tipo} style={{ marginBottom: 12, border: "1px solid #e9ecef", borderRadius: 10, overflow: "hidden" }}>
                        <div onClick={() => toggleGroup(tipo)}
                          style={{
                            display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
                            background: isOpen ? "#f8f9fa" : "#fff", cursor: "pointer", transition: "background 0.15s",
                            borderBottom: isOpen ? "1px solid #e9ecef" : "none",
                          }}>
                          <i className={`mdi ${isOpen ? "mdi-chevron-down" : "mdi-chevron-right"}`}
                            style={{ fontSize: 20, color: "#6c757d", transition: "transform 0.2s" }} />
                          <i className="mdi mdi-file-document-outline" style={{ fontSize: 18, color: tipoColor }} />
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#343a40", flex: 1 }}>{tipoLabel}</span>
                          <Badge color="dark" pill style={{ fontSize: 11 }}>{items.length} resoluciones</Badge>
                          <Badge color={items.some(r => isExpired(r.validaHasta)) ? "danger" : "success"} pill style={{ fontSize: 10 }}>
                            {items.filter(r => isExpired(r.validaHasta)).length > 0
                              ? `${items.filter(r => isExpired(r.validaHasta)).length} vencida(s)`
                              : "Todas vigentes"}
                          </Badge>
                        </div>
                        <Collapse isOpen={isOpen}>
                          <div className="table-responsive">
                            <Table className="table-hover mb-0" style={{ fontSize: 12 }}>
                              {tableHead}
                              <tbody>{items.map(renderResRow)}</tbody>
                            </Table>
                          </div>
                        </Collapse>
                      </div>
                    )
                  })}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5">
                  <i className="mdi mdi-file-search-outline" style={{ fontSize: 48, color: "#c3cbe4" }} />
                  <p className="mt-2" style={{ color: "#6c757d" }}>{searchTerm ? "No se encontraron resultados para la búsqueda" : "No hay resoluciones en esta categoría"}</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" style={{ fontSize: 12 }}>
                      {tableHead}
                      <tbody>{paginatedItems.map(renderResRow)}</tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 13, color: "#6c757d" }}>
                          Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, filtered.length)} de {filtered.length}
                        </span>
                        <select value={itemsPerPage}
                          onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1) }}
                          style={{ fontSize: 12, borderRadius: 6, border: "1px solid #ced4da", padding: "4px 8px", color: "#495057" }}>
                          <option value={10}>10 / pág</option>
                          <option value={25}>25 / pág</option>
                          <option value={50}>50 / pág</option>
                          <option value={100}>100 / pág</option>
                        </select>
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <Button size="sm" outline disabled={currentPage === 1}
                          onClick={() => setCurrentPage(1)} style={{ borderRadius: 6 }} title="Primera página">«</Button>
                        <Button size="sm" outline disabled={currentPage === 1}
                          onClick={() => setCurrentPage(p => p - 1)} style={{ borderRadius: 6 }}>‹ Anterior</Button>
                        {(() => {
                          const pages = []
                          const delta = 2
                          const left = Math.max(2, currentPage - delta)
                          const right = Math.min(totalPages - 1, currentPage + delta)
                          pages.push(<Button key={1} size="sm" color={currentPage === 1 ? "primary" : "light"} onClick={() => setCurrentPage(1)} style={{ borderRadius: 6, minWidth: 36 }}>1</Button>)
                          if (left > 2) pages.push(<span key="le" style={{ padding: "0 6px", color: "#6c757d", fontSize: 14 }}>…</span>)
                          for (let i = left; i <= right; i++) pages.push(<Button key={i} size="sm" color={currentPage === i ? "primary" : "light"} onClick={() => setCurrentPage(i)} style={{ borderRadius: 6, minWidth: 36 }}>{i}</Button>)
                          if (right < totalPages - 1) pages.push(<span key="re" style={{ padding: "0 6px", color: "#6c757d", fontSize: 14 }}>…</span>)
                          if (totalPages > 1) pages.push(<Button key={totalPages} size="sm" color={currentPage === totalPages ? "primary" : "light"} onClick={() => setCurrentPage(totalPages)} style={{ borderRadius: 6, minWidth: 36 }}>{totalPages}</Button>)
                          return pages
                        })()}
                        <Button size="sm" outline disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(p => p + 1)} style={{ borderRadius: 6 }}>Siguiente ›</Button>
                        <Button size="sm" outline disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)} style={{ borderRadius: 6 }} title="Última página">»</Button>
                        {totalPages > 10 && (
                          <div className="d-flex align-items-center gap-1 ms-2">
                            <span style={{ fontSize: 12, color: "#6c757d" }}>Ir a:</span>
                            <Input type="number" min={1} max={totalPages}
                              style={{ width: 64, fontSize: 12, borderRadius: 6, padding: "4px 8px", height: 31 }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  const val = parseInt(e.target.value)
                                  if (val >= 1 && val <= totalPages) { setCurrentPage(val); e.target.value = "" }
                                }
                              }}
                              placeholder={`${currentPage}`} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          <i className={`mdi ${modalMode === "create" ? "mdi-plus-circle" : "mdi-pencil"} me-2`} style={{ color: "#556ee6" }} />
          {modalMode === "create" ? "Nueva Resolución" : "Editar Resolución"}
        </ModalHeader>
        <ModalBody>
          <Row>
            {/* No. Resolución - hide in NC/ND create mode (both associated and manual) */}
            {!(modalMode === "create" && isNcNdTipo(formData.tipoDocumento)) && (
              <Col md={6}>
                <FormGroup>
                  <Label style={labelStyle}>No. Resolución *</Label>
                  <Input style={inputStyle} value={formData.numeroResolucion}
                    onChange={e => setFormField("numeroResolucion", e.target.value)} placeholder="18764103945575"
                    disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                </FormGroup>
              </Col>
            )}
            <Col md={modalMode === "create" && isNcNdTipo(formData.tipoDocumento) ? 6 : 3}>
              <FormGroup>
                <Label style={labelStyle}>Prefijo *</Label>
                <Input style={inputStyle} value={formData.prefijo}
                  onChange={e => setFormField("prefijo", e.target.value)} placeholder="CSK" />
              </FormGroup>
            </Col>
            <Col md={modalMode === "create" && isNcNdTipo(formData.tipoDocumento) ? 6 : 3}>
              <FormGroup>
                <Label style={labelStyle}>Tipo Documento</Label>
                <Input type="select" style={inputStyle} value={formData.tipoDocumento}
                  onChange={e => { setFormField("tipoDocumento", e.target.value); setSelectedFacturaPrefijos([]) }}>
                  <option value="">Seleccione</option>
                  {Object.entries(TIPOS_DOCUMENTO).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([code, name]) => (
                    <option key={code} value={code}>{code} - {name}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* NC/ND: Mode selector */}
          {modalMode === "create" && isNcNdTipo(formData.tipoDocumento) && (
            <div style={{ marginBottom: 16 }}>
              <div className="d-flex gap-2">
                <Button
                  color={!ncNdManualMode ? "primary" : "outline-secondary"}
                  size="sm" style={{ borderRadius: 8, flex: 1 }}
                  onClick={() => { setNcNdManualMode(false); setSelectedFacturaPrefijos([]) }}>
                  <i className="mdi mdi-link-variant me-1" />
                  Asociar a Factura existente
                </Button>
                <Button
                  color={ncNdManualMode ? "primary" : "outline-secondary"}
                  size="sm" style={{ borderRadius: 8, flex: 1 }}
                  onClick={() => { setNcNdManualMode(true); setSelectedFacturaPrefijos([]) }}>
                  <i className="mdi mdi-file-plus-outline me-1" />
                  Sin asociar (datos manuales)
                </Button>
              </div>
            </div>
          )}

          {/* NC/ND: Associate with Factura prefijos */}
          {modalMode === "create" && isNcNdTipo(formData.tipoDocumento) && !ncNdManualMode && (
            <div style={{ background: "#f0f4ff", borderRadius: 10, padding: 16, marginBottom: 16, border: "1px solid #c3cbe4" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <i className="mdi mdi-link-variant" style={{ fontSize: 20, color: "#556ee6" }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#343a40" }}>
                    Asociar a Facturas Electrónicas (01)
                  </div>
                  <div style={{ fontSize: 11, color: "#6c757d" }}>
                    El prefijo <strong>{formData.prefijo || "NC/ND"}</strong> heredará los datos (resolución, rangos, fechas, clave) de cada factura seleccionada
                  </div>
                </div>
              </div>
              {facturasElectronicas.length === 0 ? (
                <p style={{ color: "#adb5bd", textAlign: "center", margin: 8, fontSize: 13 }}>No hay resoluciones tipo 01 - Factura Electrónica</p>
              ) : (
                <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 8, padding: 6, background: "#fff" }}>
                  {facturasElectronicas.map(fac => {
                    const checked = selectedFacturaPrefijos.includes(fac.id)
                    return (
                      <div key={fac.id}
                        onClick={() => toggleFacturaPrefijo(fac.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                          borderRadius: 6, cursor: "pointer", marginBottom: 2,
                          background: checked ? "#eef2ff" : "transparent",
                          border: checked ? "1px solid #556ee6" : "1px solid transparent",
                          transition: "all 0.15s",
                        }}>
                        <input type="checkbox" checked={checked} readOnly
                          style={{ width: 16, height: 16, accentColor: "#556ee6" }} />
                        <Badge color="primary" pill style={{ fontSize: 11 }}>{fac.prefijo}</Badge>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#343a40" }}>Res. {fac.numeroResolucion}</div>
                          <div style={{ fontSize: 10, color: "#6c757d" }}>
                            Rango: {fac.rangoDesde} — {fac.rangoHasta} | Vigencia: {formatDate(fac.validaDesde)} — {formatDate(fac.validaHasta)}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {selectedFacturaPrefijos.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#556ee6" }}>
                  <i className="mdi mdi-information-outline me-1" />
                  Se crearán <strong>{selectedFacturaPrefijos.length}</strong> resolución(es) con prefijo <strong>{formData.prefijo || "NC/ND"}</strong>
                </div>
              )}
            </div>
          )}
          {/* Show form fields: always for non-NC/ND, or for NC/ND edit mode. Hidden for NC/ND manual create */}
          {(!(modalMode === "create" && isNcNdTipo(formData.tipoDocumento)) || (modalMode === "edit")) && (
            <>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Fecha Resolución</Label>
                    <Input type="date" style={inputStyle} value={formData.fechaResolucion}
                      onChange={e => setFormField("fechaResolucion", e.target.value)}
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Válida Desde</Label>
                    <Input type="date" style={inputStyle} value={formData.validaDesde}
                      onChange={e => setFormField("validaDesde", e.target.value)}
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Válida Hasta</Label>
                    <Input type="date" style={inputStyle} value={formData.validaHasta}
                      onChange={e => setFormField("validaHasta", e.target.value)}
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Rango Desde</Label>
                    <Input style={inputStyle} value={formData.rangoDesde}
                      onChange={e => setFormField("rangoDesde", e.target.value)} placeholder="75296"
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Rango Hasta</Label>
                    <Input style={inputStyle} value={formData.rangoHasta}
                      onChange={e => setFormField("rangoHasta", e.target.value)} placeholder="1000000"
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Consecutivo Actual</Label>
                    <Input style={inputStyle} value={formData.consecutivoActual}
                      onChange={e => setFormField("consecutivoActual", e.target.value)} placeholder="0" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label style={labelStyle}>Clave Técnica</Label>
                    <Input style={inputStyle} value={formData.claveTecnica}
                      onChange={e => setFormField("claveTecnica", e.target.value)} placeholder="257ca3e7dee3568fae9ba9689267a573"
                      disabled={["01","02","03","04","05"].includes(formData.tipoDocumento)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Activa</Label>
                    <div style={{ paddingTop: 6 }}>
                      <Switch onChange={v => setFormField("activo", v)} checked={formData.activo}
                        onColor="#34c38f" offColor="#c3cbe4" height={22} width={44} />
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeModal} style={{ borderRadius: 8 }}>
            Cancelar
          </Button>
          <Button color="primary" disabled={saving || (modalMode === "create" && isNcNdTipo(formData.tipoDocumento) && !ncNdManualMode && selectedFacturaPrefijos.length === 0 && facturasElectronicas.length > 0)}
            onClick={modalMode === "create" ? handleCreate : handleUpdate}
            style={{ borderRadius: 8 }}>
            {saving ? <><Spinner size="sm" /> Guardando...</> : modalMode === "create"
              ? (isNcNdTipo(formData.tipoDocumento) && selectedFacturaPrefijos.length > 0
                ? `Crear ${selectedFacturaPrefijos.length} Resolución(es)`
                : "Crear Resolución")
              : "Guardar Cambios"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Assign Prefijo to Sucursal Modal ── */}
      <Modal isOpen={assignModalOpen} toggle={() => !assigning && setAssignModalOpen(false)}>
        <ModalHeader toggle={() => !assigning && setAssignModalOpen(false)}>
          <i className="mdi mdi-link-variant me-2" style={{ color: "#556ee6" }} />
          Asignar Prefijo a Sucursales
        </ModalHeader>
        <ModalBody>
          {assignPrefijo && (
            <>
              <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#495057" }}>
                  <strong>Prefijo:</strong> <Badge color="primary" pill>{assignPrefijo.prefijo}</Badge>
                </div>
                <div style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}>
                  Resolución: {assignPrefijo.numeroResolucion} | Rango: {assignPrefijo.rangoDesde} — {assignPrefijo.rangoHasta}
                </div>
              </div>
              <Label style={labelStyle}>Seleccione las sucursales ({selectedSucursalIds.length} seleccionadas)</Label>
              <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 8, padding: 8 }}>
                {sucursales.filter(s => s.activo).map(s => {
                  const checked = selectedSucursalIds.includes(s.id)
                  return (
                    <div key={s.id}
                      onClick={() => toggleSucursalSelection(s.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                        borderRadius: 6, cursor: "pointer", marginBottom: 2,
                        background: checked ? "#eef2ff" : "transparent",
                        border: checked ? "1px solid #556ee6" : "1px solid transparent",
                        transition: "all 0.15s",
                      }}>
                      <input type="checkbox" checked={checked} readOnly
                        style={{ width: 16, height: 16, accentColor: "#556ee6" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#343a40" }}>{s.nombre}</div>
                        <div style={{ fontSize: 11, color: "#6c757d" }}>{s.codigo} — {s.ciudad || "Sin ciudad"}</div>
                      </div>
                      {s.esPrincipal && <Badge color="info" pill style={{ fontSize: 9 }}>Principal</Badge>}
                    </div>
                  )
                })}
                {sucursales.filter(s => s.activo).length === 0 && (
                  <p style={{ color: "#adb5bd", textAlign: "center", margin: 16, fontSize: 13 }}>No hay sucursales activas</p>
                )}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={assigning} onClick={() => setAssignModalOpen(false)} style={{ borderRadius: 8 }}>
            Cancelar
          </Button>
          <Button color="primary" disabled={assigning} onClick={handleAssignSucursal} style={{ borderRadius: 8 }}>
            {assigning ? <><Spinner size="sm" /> Asignando...</> : `Guardar (${selectedSucursalIds.length} sucursales)`}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Resoluciones)
